/**
 * Tech / brand icon map — maps provider keys used in EdgeLex to
 * the corresponding developer-icons React components.
 *
 * Usage:
 *   import { getTechIcon } from '@/components/icons/techIconMap';
 *   const Icon = getTechIcon('openai');
 *   if (Icon) return <Icon size={20} className="shrink-0" />;
 */

import {
  AWS,
  Azure,
  GoogleCloud,
  OneDrive,
  OpenAI,
  Anthropic,
  ClaudeAI,
  DeepSeek,
  Docker,
  Google,
  Meta,
  PostgreSQL,
  Redis,
  NodeJs,
  React as ReactIcon,
  TypeScript,
  Python,
  ChatGPT,
} from 'developer-icons';
import type { ComponentType, SVGAttributes } from 'react';

/** Props accepted by every developer-icons component */
export type TechIconProps = SVGAttributes<SVGSVGElement> & {
  size?: number;
  className?: string;
};

type TechIconComponent = ComponentType<TechIconProps>;

// ── Storage provider icons ──────────────────────────────────────────────

export const storageIconMap: Record<string, TechIconComponent> = {
  gcs: GoogleCloud,
  s3: AWS,
  azure: Azure,
  gdrive: Google,
  onedrive: OneDrive,
};

// ── AI / LLM provider icons ────────────────────────────────────────────

export const aiProviderIconMap: Record<string, TechIconComponent> = {
  openai: OpenAI,
  anthropic: Anthropic,
  claude: ClaudeAI,
  chatgpt: ChatGPT,
  deepseek: DeepSeek,
  meta: Meta,
  google: Google,
  'google-cloud': GoogleCloud,
};

// ── General tech stack icons ───────────────────────────────────────────

export const techIconMap: Record<string, TechIconComponent> = {
  // Cloud / storage
  aws: AWS,
  gcs: GoogleCloud,
  'google-cloud': GoogleCloud,
  azure: Azure,
  onedrive: OneDrive,
  gdrive: Google,

  // AI providers
  openai: OpenAI,
  anthropic: Anthropic,
  claude: ClaudeAI,
  chatgpt: ChatGPT,
  deepseek: DeepSeek,
  meta: Meta,

  // Infrastructure
  docker: Docker,
  postgresql: PostgreSQL,
  redis: Redis,
  nodejs: NodeJs,

  // Languages / frameworks
  react: ReactIcon,
  typescript: TypeScript,
  python: Python,
};

/**
 * Look up a tech brand icon by key. Returns undefined when no match exists,
 * so callers can fall back to a generic Heroicon.
 */
export function getTechIcon(key: string): TechIconComponent | undefined {
  return techIconMap[key.toLowerCase()];
}

/**
 * Storage-specific lookup.  Falls through to the general map when the
 * storage map has no entry.
 */
export function getStorageIcon(key: string): TechIconComponent | undefined {
  return storageIconMap[key.toLowerCase()] ?? techIconMap[key.toLowerCase()];
}

/**
 * AI-provider-specific lookup.  Falls through to the general map.
 */
export function getAIProviderIcon(key: string): TechIconComponent | undefined {
  return aiProviderIconMap[key.toLowerCase()] ?? techIconMap[key.toLowerCase()];
}
